import { useInfiniteQuery } from "@tanstack/react-query";
import { Check, X } from "lucide-react";
import { useEffect, useMemo } from "react";
import type { PluckSummary } from "@/components/pluck-card";
import { PluckThumbnail } from "@/components/pluck-thumbnail";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { track } from "@/lib/analytics";
import { trpc } from "@/lib/trpc";

const PAGE_SIZE = 30;
const skeletonKeys = ["a", "b", "c", "d", "e", "f"];

function PickerItem({
	pluck,
	added,
	onAdd,
}: {
	added: boolean;
	onAdd: () => void;
	pluck: PluckSummary;
}) {
	return (
		<button
			className="group relative flex flex-col overflow-hidden rounded-md border text-left transition-colors hover:border-foreground/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
			onClick={onAdd}
			title={`${pluck.sourceDomain} · <${pluck.rootTag}>`}
			type="button"
		>
			<div className="flex aspect-[4/3] w-full items-center justify-center bg-muted/60 p-1.5">
				<PluckThumbnail
					alt={pluck.sourceTitle ?? pluck.sourceDomain}
					assetId={pluck.screenshotAssetId}
					className="max-h-full max-w-full rounded-xs"
				/>
			</div>
			<p className="truncate px-1.5 py-1 text-[11px]">{pluck.sourceDomain}</p>
			{added && (
				<span className="absolute top-1 right-1 rounded-full bg-primary p-0.5 text-primary-foreground">
					<Check className="size-3" />
				</span>
			)}
		</button>
	);
}

export function LibraryPicker({
	onAdd,
	onClose,
	onBoardPluckIds,
}: {
	onAdd: (pluckId: string) => void;
	onBoardPluckIds: Set<string>;
	onClose: () => void;
}) {
	const query = useInfiniteQuery(
		trpc.plucks.list.infiniteQueryOptions(
			{ limit: PAGE_SIZE },
			{ getNextPageParam: (last) => last.nextCursor ?? undefined }
		)
	);
	const items = useMemo(
		() => query.data?.pages.flatMap((p) => p.items) ?? [],
		[query.data]
	);
	const isEmpty = !query.isPending && items.length === 0;
	useEffect(() => {
		if (isEmpty) {
			track("onboarding.empty_state_viewed", { surface: "library_picker" });
		}
	}, [isEmpty]);

	return (
		<aside className="flex w-72 shrink-0 flex-col border-l bg-card">
			<div className="flex h-12 items-center justify-between border-b px-3">
				<p className="font-medium text-sm">Add from library</p>
				<Button
					aria-label="Close"
					onClick={onClose}
					size="icon-sm"
					variant="ghost"
				>
					<X />
				</Button>
			</div>
			<div className="flex-1 overflow-auto p-3">
				{query.isPending && (
					<div className="grid grid-cols-2 gap-2">
						{skeletonKeys.map((k) => (
							<Skeleton className="aspect-[4/3]" key={k} />
						))}
					</div>
				)}
				{!query.isPending && items.length === 0 && (
					<p className="text-muted-foreground text-xs">
						Nothing in your library yet. Pluck something with the extension
						first.
					</p>
				)}
				{items.length > 0 && (
					<div className="grid grid-cols-2 gap-2">
						{items.map((pluck) => (
							<PickerItem
								added={onBoardPluckIds.has(pluck.id)}
								key={pluck.id}
								onAdd={() => onAdd(pluck.id)}
								pluck={pluck}
							/>
						))}
					</div>
				)}
				{query.hasNextPage && (
					<Button
						className="mt-3 w-full"
						disabled={query.isFetchingNextPage}
						onClick={() => {
							track("library.load_more", {
								surface: "library_picker",
								loaded: items.length,
							});
							query.fetchNextPage();
						}}
						size="sm"
						variant="outline"
					>
						{query.isFetchingNextPage ? "Loading…" : "Load more"}
					</Button>
				)}
			</div>
		</aside>
	);
}
